import type { VehicleComponent, VehicleSection, VehicleSide } from "@/lib/api/types"

// The vehicle catalogue the editors share: sections, components, sides, and
// the words for each. Mirrors the BE component sub-enums
// (`etx/server/src/entities/vehicle.entity.ts`); order follows the BE
// entity declaration so drift is easy to spot on review. Moved out of the
// full-estimate Damage step so the evidence canvas, the detail page and the
// Tasks review card all say the same thing.

export const SECTION_LABELS: Record<string, string> = {
  FRONT_BUMPER_GRILLE: "Front bumper & grille",
  REAR_BUMPER: "Rear bumper",
  FENDER: "Fender",
  FRONT_DOOR: "Front door",
  REAR_DOOR: "Rear door",
  HOOD: "Hood",
  TRUNK: "Trunk",
  ROOF: "Roof",
  FRONT_LAMPS: "Front lamps",
  REAR_LAMPS: "Rear lamps",
  QUARTER_PANEL: "Quarter panel",
  ROCKER_PANEL: "Rocker panel",
  LIFT_GATE: "Lift gate",
  TAIL_GATE: "Tail gate",
  WHEELS: "Wheels",
  FRAME: "Frame",
  TRUNK_LID: "Trunk lid",
  PICKUP_BOX: "Pickup box",
  WINDSHIELD: "Windshield",
  MISCELLANEOUS: "Other",
}

export const ALL_SECTIONS: VehicleSection[] = [
  "FRONT_BUMPER_GRILLE",
  "HOOD",
  "FENDER",
  "FRONT_LAMPS",
  "FRONT_DOOR",
  "REAR_DOOR",
  "QUARTER_PANEL",
  "ROCKER_PANEL",
  "ROOF",
  "WINDSHIELD",
  "REAR_LAMPS",
  "TRUNK_LID",
  "TRUNK",
  "LIFT_GATE",
  "TAIL_GATE",
  "REAR_BUMPER",
  "WHEELS",
  "FRAME",
  "PICKUP_BOX",
]

export const ACTION_OPTIONS = [
  { value: "REPAIR" as const, label: "Repair" },
  { value: "REPLACE" as const, label: "Replace" },
] as const

export const PART_TYPE_OPTIONS = [
  { value: "OEM" as const, label: "OEM" },
  { value: "Aftermarket" as const, label: "Aftermarket" },
] as const

export const SIDE_OPTIONS = [
  { value: "FRONT_CENTER" as const, label: "Front" },
  { value: "REAR_CENTER" as const, label: "Rear" },
  { value: "LEFT_SIDE" as const, label: "Driver" },
  { value: "RIGHT_SIDE" as const, label: "Passenger" },
  { value: "ROOF" as const, label: "Roof" },
] as const

// Mirrors the BE component sub-enums (FrontBumperGrilleComponent,
// RearBumperComponent, FenderComponent, …) from
// `etx/server/src/entities/vehicle.entity.ts`. Order intentionally
// follows the BE entity declaration so drift between the two files
// is easy to spot on review. The picker shows ONLY components in
// the user's chosen section. A BE value that appears in two
// sub-enums (LT/RT_OUTER_PANEL → QuarterPanel + PickupBox;
// BACK_UP_CAMERA → LiftGate + TailGate) is listed once per parent
// section; `componentLabel`'s `.find()` returns the first match,
// and the labels are identical so the duplicate is invisible.
export const COMPONENT_OPTIONS: Array<{
  value: VehicleComponent
  section: VehicleSection
  label: string
}> = [
  // FrontBumperGrilleComponent
  { value: "FRONT_BUMPER_COVER", section: "FRONT_BUMPER_GRILLE", label: "Front bumper cover" },
  { value: "FRONT_IMPACT_BAR", section: "FRONT_BUMPER_GRILLE", label: "Front impact bar" },
  { value: "FRONT_ENERGY_ABSORBER", section: "FRONT_BUMPER_GRILLE", label: "Front energy absorber" },
  { value: "FRONT_LOWER_COVER", section: "FRONT_BUMPER_GRILLE", label: "Front lower cover" },
  { value: "FRONT_GRILLE_ASSEMBLY", section: "FRONT_BUMPER_GRILLE", label: "Front grille assembly" },
  { value: "FRONT_UPPER_GRILLE", section: "FRONT_BUMPER_GRILLE", label: "Front upper grille" },
  { value: "FRONT_LOWER_GRILLE", section: "FRONT_BUMPER_GRILLE", label: "Front lower grille" },
  { value: "FRONT_PARK_SENSORS", section: "FRONT_BUMPER_GRILLE", label: "Front park sensors" },
  { value: "FRONT_CAMERA", section: "FRONT_BUMPER_GRILLE", label: "Front camera" },
  // RearBumperComponent
  { value: "REAR_BUMPER_COVER", section: "REAR_BUMPER", label: "Rear bumper cover" },
  { value: "REAR_IMPACT_BAR", section: "REAR_BUMPER", label: "Rear impact bar" },
  { value: "REAR_ENERGY_ABSORBER", section: "REAR_BUMPER", label: "Rear energy absorber" },
  { value: "REAR_LOWER_COVER", section: "REAR_BUMPER", label: "Rear lower cover" },
  { value: "REAR_BODY_PANEL", section: "REAR_BUMPER", label: "Rear body panel" },
  { value: "REAR_PARK_SENSORS", section: "REAR_BUMPER", label: "Rear park sensors" },
  // FenderComponent
  { value: "LT_FENDER", section: "FENDER", label: "Left fender" },
  { value: "RT_FENDER", section: "FENDER", label: "Right fender" },
  { value: "LF_FENDER_LINER", section: "FENDER", label: "Left fender liner" },
  { value: "RT_FENDER_LINER", section: "FENDER", label: "Right fender liner" },
  { value: "LF_WHEEL_OPENING_MOLDING", section: "FENDER", label: "Left wheel opening molding" },
  { value: "RF_WHEEL_OPENING_MOLDING", section: "FENDER", label: "Right wheel opening molding" },
  // FrontDoorComponent
  { value: "LT_FRONT_DOOR_SHELL", section: "FRONT_DOOR", label: "Left front door" },
  { value: "RT_FRONT_DOOR_SHELL", section: "FRONT_DOOR", label: "Right front door" },
  { value: "LT_DOOR_MIRROR", section: "FRONT_DOOR", label: "Left door mirror" },
  { value: "RT_DOOR_MIRROR", section: "FRONT_DOOR", label: "Right door mirror" },
  { value: "LF_DOOR_HANDLE", section: "FRONT_DOOR", label: "Left front door handle" },
  { value: "RF_DOOR_HANDLE", section: "FRONT_DOOR", label: "Right front door handle" },
  { value: "LF_DOOR_MOLDING", section: "FRONT_DOOR", label: "Left front door molding" },
  { value: "RF_DOOR_MOLDING", section: "FRONT_DOOR", label: "Right front door molding" },
  // RearDoorComponent
  { value: "LT_REAR_DOOR_SHELL", section: "REAR_DOOR", label: "Left rear door" },
  { value: "RT_REAR_DOOR_SHELL", section: "REAR_DOOR", label: "Right rear door" },
  { value: "LR_DOOR_HANDLE", section: "REAR_DOOR", label: "Left rear door handle" },
  { value: "RR_DOOR_HANDLE", section: "REAR_DOOR", label: "Right rear door handle" },
  { value: "LR_DOOR_MOLDING", section: "REAR_DOOR", label: "Left rear door molding" },
  { value: "RR_DOOR_MOLDING", section: "REAR_DOOR", label: "Right rear door molding" },
  // HoodComponent
  { value: "HOOD", section: "HOOD", label: "Hood" },
  // FrontLampsComponent
  { value: "LT_HEADLAMP_ASSEMBLY", section: "FRONT_LAMPS", label: "Left headlamp" },
  { value: "RT_HEADLAMP_ASSEMBLY", section: "FRONT_LAMPS", label: "Right headlamp" },
  { value: "LT_FOG_LAMP", section: "FRONT_LAMPS", label: "Left fog lamp" },
  { value: "RT_FOG_LAMP", section: "FRONT_LAMPS", label: "Right fog lamp" },
  { value: "LT_TURN_SIGNAL_LAMP", section: "FRONT_LAMPS", label: "Left turn signal lamp" },
  { value: "RT_TURN_SIGNAL_LAMP", section: "FRONT_LAMPS", label: "Right turn signal lamp" },
  // RearLampsComponent
  { value: "LT_TAIL_LAMP_ASSEMBLY", section: "REAR_LAMPS", label: "Left tail lamp" },
  { value: "RT_TAIL_LAMP_ASSEMBLY", section: "REAR_LAMPS", label: "Right tail lamp" },
  { value: "LT_TAIL_LAMP_POCKET", section: "REAR_LAMPS", label: "Left tail lamp pocket" },
  { value: "RT_TAIL_LAMP_POCKET", section: "REAR_LAMPS", label: "Right tail lamp pocket" },
  { value: "LT_BACKUP_LAMP", section: "REAR_LAMPS", label: "Left backup lamp" },
  { value: "RT_BACKUP_LAMP", section: "REAR_LAMPS", label: "Right backup lamp" },
  // QuarterPanelComponent
  { value: "LT_QUARTER_PANEL", section: "QUARTER_PANEL", label: "Left quarter panel" },
  { value: "RT_QUARTER_PANEL", section: "QUARTER_PANEL", label: "Right quarter panel" },
  { value: "LT_OUTER_PANEL", section: "QUARTER_PANEL", label: "Left outer panel" },
  { value: "RT_OUTER_PANEL", section: "QUARTER_PANEL", label: "Right outer panel" },
  { value: "LT_SIDE_PANEL", section: "QUARTER_PANEL", label: "Left side panel" },
  { value: "RT_SIDE_PANEL", section: "QUARTER_PANEL", label: "Right side panel" },
  { value: "LT_QUARTER_GLASS", section: "QUARTER_PANEL", label: "Left quarter glass" },
  { value: "RT_QUARTER_GLASS", section: "QUARTER_PANEL", label: "Right quarter glass" },
  { value: "LR_WHEEL_OPENING_MOLDING", section: "QUARTER_PANEL", label: "Left wheel opening molding" },
  { value: "RR_WHEEL_OPENING_MOLDING", section: "QUARTER_PANEL", label: "Right wheel opening molding" },
  // RockerPanelComponent
  { value: "LT_ROCKER_PANEL", section: "ROCKER_PANEL", label: "Left rocker panel" },
  { value: "RT_ROCKER_PANEL", section: "ROCKER_PANEL", label: "Right rocker panel" },
  { value: "LT_ROCKER_MOLDING", section: "ROCKER_PANEL", label: "Left rocker molding" },
  { value: "RT_ROCKER_MOLDING", section: "ROCKER_PANEL", label: "Right rocker molding" },
  // LiftGateComponent
  { value: "REAR_LIFTGATE", section: "LIFT_GATE", label: "Rear liftgate" },
  { value: "REAR_GLASS", section: "LIFT_GATE", label: "Rear glass" },
  { value: "BACK_UP_CAMERA", section: "LIFT_GATE", label: "Backup camera" },
  // TailGateComponent (BACK_UP_CAMERA shared with LiftGate)
  { value: "REAR_TAILGATE", section: "TAIL_GATE", label: "Rear tailgate" },
  { value: "BACK_UP_CAMERA", section: "TAIL_GATE", label: "Backup camera" },
  { value: "TAILGATE_MOLDING", section: "TAIL_GATE", label: "Tailgate molding" },
  // WheelsComponent
  { value: "LT_FRONT_WHEEL_RIM", section: "WHEELS", label: "Left front wheel rim" },
  { value: "RT_FRONT_WHEEL_RIM", section: "WHEELS", label: "Right front wheel rim" },
  { value: "LT_REAR_WHEEL_RIM", section: "WHEELS", label: "Left rear wheel rim" },
  { value: "RT_REAR_WHEEL_RIM", section: "WHEELS", label: "Right rear wheel rim" },
  // FrameComponent
  { value: "LT_FRONT_FRAME_RAIL", section: "FRAME", label: "Left front frame rail" },
  { value: "RT_FRONT_FRAME_RAIL", section: "FRAME", label: "Right front frame rail" },
  { value: "LT_REAR_FRAME_RAIL", section: "FRAME", label: "Left rear frame rail" },
  { value: "RT_REAR_FRAME_RAIL", section: "FRAME", label: "Right rear frame rail" },
  // TrunkLidComponent
  { value: "TRUNK_LID", section: "TRUNK_LID", label: "Trunk lid" },
  { value: "REAR_SPOILER", section: "TRUNK_LID", label: "Rear spoiler" },
  // RoofComponent
  { value: "ROOF_PANEL", section: "ROOF", label: "Roof panel" },
  { value: "SUN_ROOF", section: "ROOF", label: "Sunroof" },
  { value: "REAR_CAMERA", section: "ROOF", label: "Rear camera" },
  // PickupBoxComponent (LT/RT_OUTER_PANEL shared with QuarterPanel)
  { value: "RT_OUTER_PANEL", section: "PICKUP_BOX", label: "Right outer panel" },
  { value: "LT_OUTER_PANEL", section: "PICKUP_BOX", label: "Left outer panel" },
  { value: "RT_BEDSIDE_SIDE_ASY", section: "PICKUP_BOX", label: "Right bedside" },
  { value: "LT_BEDSIDE_SIDE_ASY", section: "PICKUP_BOX", label: "Left bedside" },
  { value: "LT_UPPER_MOLDING", section: "PICKUP_BOX", label: "Left upper molding" },
  { value: "RT_UPPER_MOLDING", section: "PICKUP_BOX", label: "Right upper molding" },
  { value: "RT_WHEEL_OPENING_MOLDING_PICKUP", section: "PICKUP_BOX", label: "Right wheel opening molding" },
  { value: "LT_WHEEL_OPENING_MOLDING_PICKUP", section: "PICKUP_BOX", label: "Left wheel opening molding" },
  // WindshieldComponent
  { value: "WINDSHIELD_GLASS", section: "WINDSHIELD", label: "Windshield glass" },
]

export function sectionLabel(section: string): string {
  return SECTION_LABELS[section] ?? section.replace(/_/g, " ").toLowerCase()
}

export function componentLabel(component: string): string {
  return (
    COMPONENT_OPTIONS.find((c) => c.value === component)?.label ??
    component.replace(/_/g, " ").toLowerCase()
  )
}

/** The five buckets a line item can sit on (what the editors pick). */
export type SideBucket = (typeof SIDE_OPTIONS)[number]["value"]

/** A photo's side (13 values) folded to the line-item bucket it belongs to. */
export function sideBucket(side: VehicleSide | string | null | undefined): SideBucket {
  const s = String(side ?? "")
  if (s === "ROOF") return "ROOF"
  if (s.startsWith("FRONT")) return "FRONT_CENTER"
  if (s.startsWith("REAR")) return "REAR_CENTER"
  if (s === "LEFT_SIDE") return "LEFT_SIDE"
  if (s === "RIGHT_SIDE") return "RIGHT_SIDE"
  return "FRONT_CENTER"
}

const SIDE_WORDS: Record<string, string> = {
  FRONT_CENTER: "Front",
  REAR_CENTER: "Rear",
  LEFT_SIDE: "Driver side",
  RIGHT_SIDE: "Passenger side",
  ROOF: "Roof",
  FRONT_LEFT_SIDE: "Front, driver side",
  FRONT_RIGHT_SIDE: "Front, passenger side",
  FRONT_LEFT_CORNER: "Front driver corner",
  FRONT_RIGHT_CORNER: "Front passenger corner",
  REAR_LEFT_SIDE: "Rear, driver side",
  REAR_RIGHT_SIDE: "Rear, passenger side",
  REAR_LEFT_CORNER: "Rear driver corner",
  REAR_RIGHT_CORNER: "Rear passenger corner",
}

/** "Front driver corner" — the side as a shop says it. */
export function sideLabel(side: VehicleSide | string | null | undefined): string {
  if (!side) return ""
  return SIDE_WORDS[side] ?? String(side).replace(/_/g, " ").toLowerCase()
}
